import { type EvalSuiteSpec } from '@sbc/domain-models/agents';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAgents } from '@/lib/api/agents';

const EMPTY_SPEC: EvalSuiteSpec = {
  name: '',
  description: '',
  agentId: null,
};

interface SuiteFormProps {
  initial?: EvalSuiteSpec;
  submitting: boolean;
  submitLabel: string;
  error?: string | null;
  onSubmit: (spec: EvalSuiteSpec) => void;
  onCancel: () => void;
}

export function SuiteForm({
  initial,
  submitting,
  submitLabel,
  error,
  onSubmit,
  onCancel,
}: SuiteFormProps) {
  const [spec, setSpec] = useState<EvalSuiteSpec>(initial ?? EMPTY_SPEC);
  const agentsQuery = useAgents({ page: 1, pageSize: 100 });
  const agents = agentsQuery.data?.items ?? [];
  const selected = agents.find((agent) => agent.id === spec.agentId);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit(spec);
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          required
          maxLength={60}
          placeholder="kbeauty"
          pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
          value={spec.name}
          onChange={(event) => setSpec((prev) => ({ ...prev, name: event.target.value }))}
        />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Unique key. Lowercase letters, numbers and dashes. Also the CLI selector (`pnpm eval
          --suite &lt;name&gt;`).
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          maxLength={2000}
          value={spec.description ?? ''}
          onChange={(event) => setSpec((prev) => ({ ...prev, description: event.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="agentId">Agent</Label>
        <Select
          required
          value={spec.agentId ?? ''}
          onValueChange={(value) => setSpec((prev) => ({ ...prev, agentId: value || null }))}
        >
          <SelectTrigger id="agentId">
            <SelectValue
              placeholder={agentsQuery.isPending ? 'Loading agents…' : 'Select an agent'}
            />
          </SelectTrigger>
          <SelectContent>
            {agents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>
                {agent.activeVersion.name} ({agent.activeVersion.slug})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          The suite runs against this agent's current active version.
        </p>
        {selected && (
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Model tier: {selected.activeVersion.modelTier} · Tools:{' '}
            {selected.activeVersion.enabledTools.length
              ? selected.activeVersion.enabledTools.join(', ')
              : 'none'}
          </p>
        )}
      </div>

      {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
